<section class="vessel-map" aria-label="Satellite vessel map">
  <div #mapContainer class="vessel-map__canvas"></div>

  <div class="vessel-map__overlay vessel-map__overlay--left">
    <div class="vessel-map__zoom vessel-map__zoom--mockup" aria-label="Map zoom controls">
      <button
        class="vessel-map__zoom-button"
        type="button"
        aria-label="Zoom in"
        [disabled]="!isReady"
        (click)="zoomIn()">+</button>
      <button
        class="vessel-map__zoom-button"
        type="button"
        aria-label="Zoom out"
        [disabled]="!isReady"
        (click)="zoomOut()">−</button>
    </div>
  </div>

  <div class="vessel-map__overlay vessel-map__mode-switch" role="group" aria-label="Map intelligence mode">
    <button
      class="vessel-map__mode-button"
      type="button"
      [attr.aria-pressed]="mapMode === 'live'"
      [class.vessel-map__mode-button--active]="mapMode === 'live'"
      [disabled]="!isReady"
      (click)="setMapMode('live')">LIVE</button>
    <button
      class="vessel-map__mode-button vessel-map__mode-button--dark"
      type="button"
      [attr.aria-pressed]="mapMode === 'dark'"
      [class.vessel-map__mode-button--active]="mapMode === 'dark'"
      [disabled]="!isReady"
      (click)="setMapMode('dark')">DARK SHIP</button>
  </div>

  <div
    class="vessel-map__selection-surface"
    *ngIf="isSelectingArea"
    (pointerdown)="beginAreaDrag($event)"
    (pointermove)="updateAreaDrag($event)"
    (pointerup)="completeAreaDrag($event)"
    (pointercancel)="completeAreaDrag($event)">
    <div
      class="vessel-map__selection-rectangle"
      *ngIf="areaSelection"
      [style.height.px]="areaSelection.height"
      [style.left.px]="areaSelection.left"
      [style.top.px]="areaSelection.top"
      [style.width.px]="areaSelection.width"></div>
  </div>

  <div class="vessel-map__hint" *ngIf="areaHint">{{ areaHint }}</div>

  <section class="vessel-map__data-panel" *ngIf="activeDataPanel === 'history'" aria-label="Past track controls">
    <header><strong>Past track · {{ selectedVesselName }}</strong><button type="button" aria-label="Close past track" (click)="closeDataPanel()">×</button></header>
    <div class="vessel-map__date-controls">
      <label>From<input type="date" [value]="historyFrom" (change)="updateHistoryFrom($event)"></label>
      <label>To<input type="date" [value]="historyTo" (change)="updateHistoryTo($event)"></label>
      <button type="button" (click)="applyHistoryRange()">Show</button>
    </div>
    <p>{{ historyMessage }}</p>
    <div class="vessel-map__point-info" *ngIf="focusedHistoryPoint as point">
      <b>{{ formatMapDate(point.lastPositionUtc) }}</b>
      <span>{{ formatMapValue(point.latitude, '', 4) }}, {{ formatMapValue(point.longitude, '', 4) }}</span>
      <span>Speed {{ formatMapValue(point.speed, ' kn') }} · Course {{ formatMapValue(point.course, '°') }}</span>
    </div>
  </section>

  <section class="vessel-map__data-panel" *ngIf="activeDataPanel === 'route'" aria-label="Calculated route details">
    <header><strong>Calculated route</strong><button type="button" aria-label="Close calculated route" (click)="closeDataPanel()">×</button></header>
    <p>{{ selectedVesselName }}<span *ngIf="routeDestinationLabel"> → {{ routeDestinationLabel }}</span><span *ngIf="routeData?.distanceNm !== null && routeData?.distanceNm !== undefined"> · {{ formatMapValue(routeData?.distanceNm, ' NM') }}</span></p>
    <p class="vessel-map__panel-note">Illustrative calculated route; not navigation guidance.</p>
  </section>

  <section class="vessel-map__data-panel" *ngIf="activeDataPanel === 'estimated'" aria-label="Estimated position details">
    <header><strong>Estimated position · SAT‑E</strong><button type="button" aria-label="Close estimated position" (click)="closeDataPanel()">×</button></header>
    <p>{{ selectedVesselName }} · IMO {{ selectedVesselImo.replace('IMO ', '') }}</p>
    <p>Coordinates · {{ formatMapValue(estimatedPositionData?.latitude, '', 4) }}, {{ formatMapValue(estimatedPositionData?.longitude, '', 4) }}</p>
    <p>Estimate updated · {{ formatMapDate(estimatedPositionUpdatedAt) }}</p>
    <p class="vessel-map__panel-note">SAT‑E estimate; the last observed AIS position remains distinct.</p>
  </section>

  <section class="vessel-map__weather-panel" *ngIf="isWeatherOpen && weatherData as weather" aria-label="Weather near vessel">
    <header><strong>{{ weatherLocation?.label === 'AOI centre' ? 'Weather in selected area' : 'Weather near vessel' }}</strong><button type="button" aria-label="Close weather" (click)="closeWeather()">×</button></header>
    <p>{{ weatherLocation?.label === 'AOI centre' ? 'AOI centre' : selectedVesselName }} · {{ weatherLocation?.latitude | number:'1.3-3' }}, {{ weatherLocation?.longitude | number:'1.3-3' }}</p>
    <div class="vessel-map__weather-metrics">
      <div><label>Wind · {{ formatMapValue(weather.windDirectionDegrees, '°', 0) }}</label><b>{{ formatMapValue(weather.windSpeedKnots, ' kn') }}</b></div>
      <div><label>Wave height</label><b>{{ formatMapValue(weather.waveHeightMeters, ' m') }}</b></div>
      <div><label>Air temperature</label><b>{{ formatMapValue(weather.temperatureCelsius, ' °C') }}</b></div>
      <div><label>Visibility</label><b>{{ weather.visibilityMeters === null ? 'Not reported' : formatMapValue(weather.visibilityMeters / 1000, ' km') }}</b></div>
    </div>
    <button class="vessel-map__weather-details" type="button" (click)="weatherDetailsExpanded = !weatherDetailsExpanded">{{ weatherDetailsExpanded ? 'Hide weather details' : 'Weather details' }}</button>
    <div class="vessel-map__weather-extra" *ngIf="weatherDetailsExpanded">
      <div><span>Sea temperature</span><b>{{ formatMapValue(weather.seaTemperatureCelsius, ' °C') }}</b></div>
      <div><span>Pressure</span><b>{{ formatMapValue(weather.pressureHpa, ' hPa') }}</b></div>
    </div>
    <p class="vessel-map__panel-note">Data updated · {{ formatMapDate(weather.time) }}</p>
  </section>

  <div class="vessel-map__actions" appTutorialAnchor="search-area" *ngIf="isReady && !mapError">
    <button
      class="vessel-map__action"
      [class.vessel-map__action--dark]="mapMode === 'dark'"
      type="button"
      (click)="mapMode === 'live' && areaSearchAvailable ? searchSelectedArea() : startAreaSelection()">
      {{ mapMode === 'dark' ? 'DRAW AOI' : (areaSearchAvailable ? 'SEARCH THIS AREA' : 'SELECT AREA') }}
    </button>
  </div>

  <div class="vessel-map__state" role="status" aria-live="polite" *ngIf="isLoading">
    <span class="vessel-map__spinner" aria-hidden="true"></span>
    Loading satellite map…
  </div>

  <div class="vessel-map__state vessel-map__state--error" role="alert" *ngIf="errorMessage && mapError">
    <strong>Satellite map unavailable</strong>
    <span>{{ errorMessage }}</span>
  </div>

  <div class="vessel-map__state vessel-map__state--notice" role="alert" *ngIf="errorMessage && !mapError">
    <span>{{ errorMessage }}</span>
  </div>
</section>
